import { useCallback, useEffect, useRef, useState } from 'react';
import { clamp } from 'lodash';
const DEFAULT_MAX_SCALE = 3;
const BUTTON_STEP = 1.5;
const DOUBLE_TAP_SCALE = 2;
const DOUBLE_TAP_MAX_DELAY = 300;
const DOUBLE_TAP_MAX_MOVE = 30;
// Float slack, so a scale of 1.0000001 does not read as zoomed
const ZOOM_TOLERANCE = 0.01;
const distanceBetween = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
const midpointOf = (a, b) => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2
});

// Pinch and pan zoom inside the element's own box (CON-8444), no fullscreen.
// Paging between photos belongs to the scroll-snap container, not here.
// Origin is top-left, so holding a focal point still is x' = f - (f - x) * s'/s.
// Transforms are written to the DOM directly since touchmove fires every frame.
const usePinchZoom = ({
  maxScale = DEFAULT_MAX_SCALE,
  onGestureZoom,
  onTap
} = {}) => {
  // State, not refs: the gallery moves these between slides, and a ref swap
  // would not re-run the effect, so listeners would stay on the first photo
  const [container, setContainer] = useState(null);
  const [content, setContent] = useState(null);
  const containerRef = useCallback(node => setContainer(node), []);
  const contentRef = useCallback(node_0 => setContent(node_0), []);
  const transformRef = useRef({
    scale: 1,
    x: 0,
    y: 0
  });
  const gestureRef = useRef(null);
  const lastTapRef = useRef(null);
  const tapTimeoutRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Held in refs so a new callback identity doesn't rebind the touch listeners
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;
  // Fired once per completed pinch or double-tap, never per frame (CON-8512)
  const onGestureZoomRef = useRef(onGestureZoom);
  onGestureZoomRef.current = onGestureZoom;

  // Clamps so the scaled image can never pull its edges inside the frame
  const commit = useCallback((next, animate) => {
    if (!container) {
      return;
    }
    const {
      width,
      height
    } = container.getBoundingClientRect();
    const nextScale = clamp(next.scale, 1, maxScale);
    const x = clamp(next.x, width - width * nextScale, 0);
    const y = clamp(next.y, height - height * nextScale, 0);
    transformRef.current = {
      scale: nextScale,
      x,
      y
    };
    // A step, not the raw scale: publishing raw would re-render the gallery
    // every frame, and consumers only need the bucket
    setScale(prev => {
      const atLimit = nextScale >= maxScale - ZOOM_TOLERANCE;
      const step = atLimit ? maxScale : Math.ceil(nextScale);
      return prev === step ? prev : step;
    });

    // Absent while a video is selected, but the state above still has to settle
    if (content) {
      content.style.transition = animate ? 'transform 200ms ease-out' : 'none';
      content.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${nextScale})`;
    }
  }, [container, content, maxScale]);
  const zoomTo = useCallback((nextScale_0, focal) => {
    if (!container) {
      return;
    }
    const {
      width: width_0,
      height: height_0
    } = container.getBoundingClientRect();
    const point = focal ?? {
      x: width_0 / 2,
      y: height_0 / 2
    };
    const {
      current
    } = transformRef;
    const target = clamp(nextScale_0, 1, maxScale);
    const ratio = target / current.scale;
    commit({
      scale: target,
      x: point.x - (point.x - current.x) * ratio,
      y: point.y - (point.y - current.y) * ratio
    }, true);
  }, [commit, container, maxScale]);
  const zoomIn = useCallback(() => zoomTo(transformRef.current.scale * BUTTON_STEP), [zoomTo]);
  const zoomOut = useCallback(() => zoomTo(transformRef.current.scale / BUTTON_STEP), [zoomTo]);
  const reset = useCallback(() => {
    gestureRef.current = null;
    commit({
      scale: 1,
      x: 0,
      y: 0
    }, true);
  }, [commit]);
  useEffect(() => {
    if (!container) {
      return undefined;
    }
    const startPan = touch => ({
      type: 'pan',
      start: {
        ...transformRef.current
      },
      origin: {
        x: touch.clientX,
        y: touch.clientY
      },
      moved: false
    });

    // At 1x the scroll-snap container handles dragging, so a single finger is
    // only tracked to tell a tap apart from a scroll
    const startTrack = touch_0 => ({
      type: 'track',
      origin: {
        x: touch_0.clientX,
        y: touch_0.clientY
      },
      moved: false
    });
    const handleTouchStart = event => {
      if (event.touches.length === 2) {
        gestureRef.current = {
          type: 'pinch',
          start: {
            ...transformRef.current
          },
          distance: distanceBetween(event.touches[0], event.touches[1]),
          origin: midpointOf(event.touches[0], event.touches[1])
        };
      } else if (event.touches.length === 1) {
        gestureRef.current = transformRef.current.scale > 1 ? startPan(event.touches[0]) : startTrack(event.touches[0]);
      }
    };
    const handleTouchMove = event_0 => {
      const gesture = gestureRef.current;
      if (!gesture) {
        return;
      }
      if (gesture.type === 'pinch' && event_0.touches.length === 2) {
        event_0.preventDefault();
        const rect = container.getBoundingClientRect();
        const midpoint = midpointOf(event_0.touches[0], event_0.touches[1]);
        const nextScale_1 = clamp(gesture.start.scale * distanceBetween(event_0.touches[0], event_0.touches[1]) / gesture.distance, 1, maxScale);
        const ratio_0 = nextScale_1 / gesture.start.scale;
        // Focal point held still, plus whatever the midpoint itself travelled
        const focalX = gesture.origin.x - rect.left;
        const focalY = gesture.origin.y - rect.top;
        commit({
          scale: nextScale_1,
          x: focalX - (focalX - gesture.start.x) * ratio_0 + (midpoint.x - gesture.origin.x),
          y: focalY - (focalY - gesture.start.y) * ratio_0 + (midpoint.y - gesture.origin.y)
        }, false);
      } else if (gesture.type === 'pan' && event_0.touches.length === 1) {
        event_0.preventDefault();
        const dx = event_0.touches[0].clientX - gesture.origin.x;
        const dy = event_0.touches[0].clientY - gesture.origin.y;
        if (Math.hypot(dx, dy) > DOUBLE_TAP_MAX_MOVE) {
          gesture.moved = true;
        }
        commit({
          scale: gesture.start.scale,
          x: gesture.start.x + dx,
          y: gesture.start.y + dy
        }, false);
      } else if (gesture.type === 'track' && event_0.touches.length === 1) {
        // No preventDefault: the drag has to stay a page scroll or a snap scroll
        const dx_0 = event_0.touches[0].clientX - gesture.origin.x;
        const dy_0 = event_0.touches[0].clientY - gesture.origin.y;
        if (Math.hypot(dx_0, dy_0) > DOUBLE_TAP_MAX_MOVE) {
          gesture.moved = true;
        }
      }
    };
    const handleTouchEnd = event_1 => {
      const gesture_0 = gestureRef.current;

      // A pinch is over as soon as it drops below two fingers, whether one
      // finger lifts or both, so report it here rather than at the tap logic
      if (gesture_0?.type === 'pinch' && transformRef.current.scale !== gesture_0.start.scale) {
        onGestureZoomRef.current?.();
      }
      if (event_1.touches.length === 1) {
        // Lifting one finger out of a pinch carries on as a pan, or goes back
        // to plain tracking if that pinch ended at 1x
        gestureRef.current = transformRef.current.scale > 1 ? startPan(event_1.touches[0]) : startTrack(event_1.touches[0]);
        return;
      }
      gestureRef.current = null;
      if (event_1.touches.length > 0 || gesture_0?.type === 'pinch') {
        return;
      }
      const touch_1 = event_1.changedTouches[0];
      if (!touch_1 || gesture_0?.moved) {
        lastTapRef.current = null;
        return;
      }
      const previous = lastTapRef.current;
      const isDoubleTap = previous && event_1.timeStamp - previous.time < DOUBLE_TAP_MAX_DELAY && Math.hypot(touch_1.clientX - previous.x, touch_1.clientY - previous.y) < DOUBLE_TAP_MAX_MOVE;
      if (isDoubleTap) {
        lastTapRef.current = null;
        // Drop the pending single tap, or zooming would also flash the arrows
        clearTimeout(tapTimeoutRef.current);
        const rect_0 = container.getBoundingClientRect();
        if (transformRef.current.scale > 1) {
          reset();
        } else {
          zoomTo(DOUBLE_TAP_SCALE, {
            x: touch_1.clientX - rect_0.left,
            y: touch_1.clientY - rect_0.top
          });
        }
        onGestureZoomRef.current?.();
        return;
      }
      lastTapRef.current = {
        time: event_1.timeStamp,
        x: touch_1.clientX,
        y: touch_1.clientY
      };

      // Held until the double-tap window closes so only a real single tap counts
      clearTimeout(tapTimeoutRef.current);
      tapTimeoutRef.current = setTimeout(() => {
        onTapRef.current?.();
      }, DOUBLE_TAP_MAX_DELAY);
    };
    const handleTouchCancel = () => {
      gestureRef.current = null;
    };

    // touchmove must be non-passive. A passive listener cannot preventDefault
    // the browser's own page zoom, which is the bug reported in round 2.
    container.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false
    });
    container.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });
    container.addEventListener('touchcancel', handleTouchCancel, {
      passive: true
    });
    return () => {
      clearTimeout(tapTimeoutRef.current);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [commit, container, maxScale, reset, zoomTo]);

  // Rotating the device changes the box, so re-clamp against the new bounds
  useEffect(() => {
    const handleResize = () => commit(transformRef.current, false);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [commit]);
  return {
    canZoomIn: scale < maxScale - ZOOM_TOLERANCE,
    canZoomOut: scale > 1 + ZOOM_TOLERANCE,
    containerRef,
    contentRef,
    isZoomed: scale > 1 + ZOOM_TOLERANCE,
    reset,
    // A whole-number step, not the live scale. Enough to pick an image
    // candidate; the live value stays in the DOM transform.
    scaleStep: scale,
    zoomIn,
    zoomOut
  };
};
export default usePinchZoom;